import type { App, Location, TermsAndConditionsStateDTO } from "@pentacode/core";
import { clamp, dateAdd } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import type { AcceptTermsDialog } from "../elements/terms/accept-terms-dialog";
import { restart } from "./restart";

const loaded: Map<string, Promise<unknown>> = new Map<string, Promise<unknown>>();

export function loadScript<TScript>(src: string, global: string): Promise<TScript> {
    if (loaded.has(src)) {
        return loaded.get(src) as Promise<TScript>;
    }

    const scriptElement = document.createElement("script");
    scriptElement.src = src;
    scriptElement.type = "text/javascript";
    const promise = new Promise<TScript>((resolve, reject) => {
        scriptElement.onload = () => resolve(window[global as keyof typeof window]);
        scriptElement.onerror = (e: Event) => reject(e);
        document.head.appendChild(scriptElement);
    });

    loaded.set(src, promise);
    return promise;
}

export function saveFile(name: string, type: string, contents: string) {
    return downloadFromUrl(`data:${type};base64,${btoa(unescape(encodeURIComponent(contents)))}`, name);
}

export function downloadFromUrl(url: string, name: string) {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.rel = "noopener";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

export function getActiveElement(root: DocumentOrShadowRoot = document): Element | null {
    const active = root.activeElement;
    return active?.shadowRoot ? getActiveElement(active.shadowRoot) : active;
}

export function isCursorInInput() {
    const activeElement = getActiveElement();
    return activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement;
}

export function insertAtCursor(input: HTMLInputElement | HTMLTextAreaElement, text: string) {
    if (input.selectionStart || input.selectionStart === 0) {
        const startPos = input.selectionStart;
        const endPos = input.selectionEnd;
        input.value =
            input.value.substring(0, startPos) + text + input.value.substring(endPos || startPos, input.value.length);
        input.selectionStart = startPos + text.length;
        input.selectionEnd = startPos + text.length;
    } else {
        input.value += text;
    }
}

export const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export function readFileAsDataURL(blob: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result as string);
        };

        reader.onerror = (e) => {
            reader.abort();
            reject(e);
        };

        reader.readAsDataURL(blob);
    });
}

export function selectFile() {
    return new Promise<File | null>((resolve) => {
        const input = document.createElement("input");
        input.style.opacity = "0";
        input.style.position = "absolute";
        input.type = "file";
        document.body.appendChild(input);
        input.addEventListener(
            "change",
            () => {
                resolve(input.files?.[0] || null);
            },
            { once: true }
        );
        input.click();
        document.body.removeChild(input);
    });
}

export function decodeText(buffer: ArrayBuffer) {
    try {
        return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    } catch {
        return new TextDecoder("windows-1252").decode(buffer);
    }
}

export function formatFileSize(size: number = 0) {
    return size < 1e6 ? Math.ceil(size / 10) / 100 + " KB" : Math.ceil(size / 10000) / 100 + " MB";
}

export function getBoundingClientRectWithoutTransforms(el: HTMLElement) {
    let left = 0;
    let top = 0;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    while (el) {
        left += el.offsetLeft;
        top += el.offsetTop;

        el = el.offsetParent as HTMLElement;
    }

    return { left, top, width, height };
}

export async function unzip(file: ArrayBuffer | Uint8Array | Blob): Promise<ArrayBuffer[]> {
    const JSZip = await (await import("jszip")).default;
    const zip = await JSZip.loadAsync(file);
    const files = Promise.all(
        Object.values(zip.files)
            .filter((file) => !file.dir)
            .map((file) => file.async("arraybuffer"))
    );
    return files;
}

export async function asSinglePDF(files: ArrayBuffer[]) {
    const { PDFDocument } = await import("pdf-lib");

    const pdf = await PDFDocument.create();

    for (const file of files) {
        try {
            const source = await PDFDocument.load(file, { ignoreEncryption: true });
            const pages = await pdf.copyPages(source, source.getPageIndices());
            pages.forEach((page) => pdf.addPage(page));
        } catch (e) {
            console.log(e);
        }
    }

    return pdf.save({});
}

export async function getLocation() {
    return new Promise<Location>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            ({ coords: { latitude, longitude, accuracy } }) => {
                resolve({ latitude, longitude, accuracy });
            },
            (e) => reject(e),
            {
                enableHighAccuracy: true,
            }
        );
    });
}

export async function getLocationStatus(): Promise<"unavailable" | "available" | "granted" | "denied"> {
    if (!("geolocation" in navigator) || !("permissions" in navigator)) {
        return "unavailable";
    }

    const res = await navigator.permissions.query({ name: "geolocation" });

    switch (res.state) {
        case "granted":
        case "denied":
            return res.state;
        default:
            return "available";
    }
}

export function getMonthsForCommittingTimes(app: App, currentMonth: DateString) {
    let initialMonth = app.account?.admin
        ? dateAdd(currentMonth, { years: -2 })
        : app.company?.settings.commitTimeEntriesBefore || dateAdd(currentMonth, { years: -1 });

    const months: DateString[] = [];

    while (initialMonth < currentMonth) {
        initialMonth = dateAdd(initialMonth, { months: 1 });
        months.push(initialMonth);
    }
    return months;
}

export async function showAcceptTermsDialog(
    dialog: AcceptTermsDialog,
    app: App,
    termsState: TermsAndConditionsStateDTO
) {
    const result = await dialog.show(termsState);

    if (result) {
        await app.api.acceptTermsAndConditionsAndProcessingAgreement();
    } else {
        const acceptanceNeeded = new Date() > new Date(termsState.deadline);
        if (acceptanceNeeded) {
            await app.logout();
            restart();
        } else {
            await app.update({ termsDismissalTimestamp: new Date() });
        }
    }
}

/**
 * Triggers a reflow (layout recalculation) on the given element.
 */
export function triggerReflow(el: HTMLElement) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetHeight; // accessing offsetHeight triggers a reflow, see https://gist.github.com/paulirish/5d52fb081b3570c81e3a
}

/**
 * Moves an item in a list from one index to another, clamping the indices to the list's bounds.
 */
export function moveClamped<T>(list: Array<T>, from: number, to: number): Array<T> {
    const clampedFrom = clamp(list.length, 0, from);
    const clampedTo = clamp(list.length, 0, to);

    const length = list.length;
    const result = list.slice();

    const item = result.splice(clampedFrom, 1)[0];

    return [...result.slice(0, clampedTo), item, ...result.slice(clampedTo, length)];
}

/**
 * "Debounces" a function, making sure it is only called once within a certain
 * time window
 */
export function debounce<F extends (...args: unknown[]) => unknown>(
    fn: F,
    delay: number,
    keyFn: (...args: Parameters<F>) => string | number = () => 0
) {
    const timeouts = new Map<string | number, number>();

    return function (...args: Parameters<F>) {
        const key = keyFn(...args);
        const timeout = timeouts.get(key);
        clearTimeout(timeout);
        timeouts.set(
            key,
            window.setTimeout(() => fn(...args), delay)
        );
    };
}
